// Category-specific Filter Panel Component
import React, { useCallback, useTransition } from 'react';
import clsx from 'clsx';
import styles from './CategoryFilterPanel.module.css';
import type { VettingStatus } from '../types/vetting';
import { useBucketStatistics } from '../hooks/useCategoriesData';

/**
 * Category Filter State Type
 *
 * Maps to URL parameters for persistence and sharing:
 * - `has_bisac` → URL param `bisac` (true/false)
 * - `has_display_name` → URL param `display` ('has'/'missing')
 * - `has_bucket_level_1` → URL param `bucket` ('has'/'missing')
 * - `bucket_level_2` → URL param `bucket2` (comma-separated bucket types)
 * - `vetting_statuses` → URL param `vet` (comma-separated: 'green','yellow','red')
 */
export type CategoryFilters = {
  // BISAC mapping
  has_bisac?: boolean;

  // Vetting System
  vetting_statuses?: VettingStatus[];

  // Display name
  has_display_name?: boolean;

  // Bucket assignment
  has_bucket_level_1?: boolean;

  // Bucket type selectors (multi-select)
  bucket_level_2?: string[];
};

interface CategoryFilterPanelProps {
  filters: CategoryFilters;
  onFiltersChange: (filters: CategoryFilters) => void;
  categoryCount?: number;
  isLoading?: boolean;
  className?: string;
  onSyncJudgingCounts?: () => void;
  syncingJudgingCounts?: boolean;
}

// Local helper to count active filters for summary display
const getActiveFilterCount = (filters: CategoryFilters): number => {
  let count = 0;

  if (filters.has_bisac !== undefined) count++;
  if (filters.vetting_statuses && filters.vetting_statuses.length > 0 && filters.vetting_statuses.length < 3) count++;
  if (filters.has_display_name !== undefined) count++;
  if (filters.has_bucket_level_1 !== undefined) count++;
  if (filters.bucket_level_2 && filters.bucket_level_2.length > 0) count++;

  return count;
};

const VETTING_STATUS_ORDER: VettingStatus[] = ['green', 'yellow', 'red'];
const VETTING_LABELS: Record<VettingStatus, string> = {
  green: 'Approved',
  yellow: 'Review',
  red: 'Not suitable'
};

const BOOLEAN_FILTERS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

const BUCKET_LEVEL_1_OPTIONS = [
  'Adult',
  'Children',
  'Young Adult'
];

const BUCKET_LEVEL_2_OPTIONS = [
  'Fiction',
  'Non-Fiction',
  'Poetry',
  'Technical'
];

export const CategoryFilterPanel: React.FC<CategoryFilterPanelProps> = ({
  filters,
  onFiltersChange,
  categoryCount,
  isLoading = false,
  className = '',
  onSyncJudgingCounts,
  syncingJudgingCounts = false
}) => {
  const [collapsed, setCollapsed] = React.useState(true);
  const [, startTransition] = useTransition();
  const activeFilterCount = getActiveFilterCount(filters);
  const vettingSelectedSet = React.useMemo(() => new Set(filters.vetting_statuses ?? []), [filters.vetting_statuses]);
  const vettingAllSelected = !filters.vetting_statuses || filters.vetting_statuses.length === 0 || filters.vetting_statuses.length === 3;
  const { data: bucketStats } = useBucketStatistics();

  const updateFilters = useCallback((updates: Partial<CategoryFilters>) => {
    const hasChanges = Object.entries(updates).some(([key, value]) => {
      const typedKey = key as keyof CategoryFilters;
      return !Object.is(filters[typedKey], value);
    });

    if (!hasChanges) {
      return;
    }

    startTransition(() => {
      onFiltersChange({ ...filters, ...updates });
    });
  }, [filters, onFiltersChange, startTransition]);

  const renderBooleanButtons = (
    options: Array<{ value: string; label: string }>,
    current: boolean | undefined,
    onSelect: (value: boolean | undefined) => void
  ) => (
    <div className={styles.buttonRow}>
      {options.map((option) => {
        const isActive = option.value === 'all'
          ? current === undefined
          : option.value === 'yes'
            ? current === true
            : current === false;

        return (
          <button
            key={option.value}
            type="button"
            className={clsx(
              styles.chipButton,
              isActive && styles.chipButtonActive
            )}
            onClick={() => {
              if (option.value === 'all') {
                onSelect(undefined);
              } else {
                onSelect(option.value === 'yes');
              }
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={clsx(styles.root, className)}>
      <div className={styles.header}>
        <div className={styles.title}>
          🔍 Filters
          <button
            type="button"
            className={styles.toggleButton}
            onClick={() => setCollapsed((prev) => !prev)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Show filters' : 'Hide filters'}
          >
            {collapsed ? 'Show ▼' : 'Hide ▲'}
          </button>
        </div>
        <div className={styles.statusInfo}>
          {onSyncJudgingCounts && (
            <button
              type="button"
              className={styles.syncButton}
              onClick={onSyncJudgingCounts}
              disabled={syncingJudgingCounts}
            >
              {syncingJudgingCounts ? 'Syncing…' : 'Sync Judging Counts'}
            </button>
          )}
          {categoryCount !== undefined && (
            <span className={styles.statusText}>
              {isLoading ? '⏳ Loading…' : (
                <>
                  {categoryCount} categor{categoryCount !== 1 ? 'ies' : 'y'}
                </>
              )}
            </span>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className={styles.grid}>
          <div className={styles.group}>
            <label className={styles.label}>BISAC Mapping</label>
            {renderBooleanButtons(
              BOOLEAN_FILTERS,
              filters.has_bisac,
              (value) => updateFilters({ has_bisac: value })
            )}
          </div>

          <div className={styles.group}>
            <label className={styles.label}>Display Name</label>
            {renderBooleanButtons(
              BOOLEAN_FILTERS,
              filters.has_display_name,
              (value) => updateFilters({ has_display_name: value })
            )}
          </div>

          <div className={styles.group}>
            <label className={styles.label}>Bucket Assignment</label>
            {renderBooleanButtons(
              BOOLEAN_FILTERS,
              filters.has_bucket_level_1,
              (value) => updateFilters({ has_bucket_level_1: value })
            )}
          </div>

          <div className={styles.group}>
            <label className={styles.label}>Vetting Status</label>
            <div className={styles.buttonRow}>
              <button
                type="button"
                className={clsx(
                  styles.chipButton,
                  styles.vettingButton,
                  vettingAllSelected && styles.vettingButtonAllActive
                )}
                onClick={() => updateFilters({ vetting_statuses: undefined })}
              >
                All
              </button>
              {VETTING_STATUS_ORDER.map((status) => {
                const isActive = vettingSelectedSet.has(status);
                return (
                  <button
                    key={status}
                    type="button"
                    className={clsx(
                      styles.chipButton,
                      styles.vettingButton,
                      status === 'green' && isActive && styles.vettingButtonApprovedActive,
                      status === 'yellow' && isActive && styles.vettingButtonReviewActive,
                      status === 'red' && isActive && styles.vettingButtonCriticalActive
                    )}
                    onClick={() => {
                      const next = new Set(filters.vetting_statuses ?? []);
                      if (next.has(status)) {
                        if (next.size === 1) {
                          return;
                        }
                        next.delete(status);
                      } else {
                        next.add(status);
                      }
                      const ordered = VETTING_STATUS_ORDER.filter((item) => next.has(item));
                      updateFilters({
                        vetting_statuses:
                          ordered.length === VETTING_STATUS_ORDER.length ? undefined : ordered
                      });
                    }}
                  >
                    {VETTING_LABELS[status]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.group} style={{ gridColumn: '1 / -1' }}>
            <label className={styles.label}>Audience</label>
            <div className={styles.bucketGrid}>
              {BUCKET_LEVEL_1_OPTIONS.map((audience) => {
                const bucketCount = bucketStats?.by_bucket_level_1
                  .find((b) => b.bucket_level_1 === audience)?.category_count ?? 0;

                return (
                  <button
                    key={audience}
                    type="button"
                    className={styles.bucketCard}
                    disabled
                    style={{ cursor: 'default', opacity: 1 }}
                  >
                    <span className={styles.bucketCardLabel}>{audience}</span>
                    {bucketCount > 0 && (
                      <span className={styles.bucketCardCount}>{bucketCount.toLocaleString()}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.group} style={{ gridColumn: '1 / -1' }}>
            <label className={styles.label}>Content Type</label>
            <div className={styles.bucketGrid}>
              {BUCKET_LEVEL_2_OPTIONS.map((bucket) => {
                const isSelected = (filters.bucket_level_2 ?? []).includes(bucket);
                const bucketCount = bucketStats?.by_bucket_level_2
                  .filter((b) => b.bucket_level_2 === bucket)
                  .reduce((sum, b) => sum + b.category_count, 0) ?? 0;

                return (
                  <button
                    key={bucket}
                    type="button"
                    className={clsx(
                      styles.bucketCard,
                      isSelected && styles.bucketCardActive
                    )}
                    onClick={() => {
                      const current = filters.bucket_level_2 ?? [];
                      const next = current.includes(bucket)
                        ? current.filter((b) => b !== bucket)
                        : [...current, bucket];
                      updateFilters({
                        bucket_level_2: next.length > 0 ? next : undefined
                      });
                    }}
                  >
                    <span className={styles.bucketCardLabel}>{bucket}</span>
                    {bucketCount > 0 && (
                      <span className={styles.bucketCardCount}>{bucketCount.toLocaleString()}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <div className={styles.summary}>
          {activeFilterCount > 0
            ? `${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active`
            : 'No filters applied'}
        </div>
      </div>
    </div>
  );
};

export default CategoryFilterPanel;
